import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { ApiService } from '../../core/api.service';
import { I18nService, SupportedDateFormat, SupportedLanguage } from '../../core/i18n.service';
import { NotificationPreferences, PlanEntitlements } from '../../core/models';
import { ThemeService } from '../../core/theme.service';
import { countryDisplayName, countryFlag } from '../../core/countries';
import { CountryCode, getCountries, getCountryCallingCode, parsePhoneNumberFromString } from 'libphonenumber-js';
import { LoadingSpinner } from '../loading-spinner/loading-spinner';
import { SessionService } from '../../core/session.service';
import { isTrustedHttpsHostUrl } from '../../core/url-security';

type BooleanPreferenceKey = {
  [K in keyof NotificationPreferences]-?: NonNullable<NotificationPreferences[K]> extends boolean ? K : never
}[keyof NotificationPreferences];

type TextPreferenceKey = {
  [K in keyof NotificationPreferences]-?: NonNullable<NotificationPreferences[K]> extends string ? K : never
}[keyof NotificationPreferences];

interface NotificationToggleSetting {
  key: string;
  preferenceKey: BooleanPreferenceKey;
  proOnly?: boolean;
  disabled?: boolean;
}

interface SmsCountryOption {
  code: CountryCode;
  name: string;
  dialCode: string;
}


@Component({ selector: 'app-settings', standalone: true, imports: [FormsModule, RouterLink, LoadingSpinner], templateUrl: './settings.html', styleUrl: './settings.scss' })
export class Settings implements OnInit, OnDestroy {
  private static readonly SMS_VERIFICATION_RESEND_COOLDOWN_SECONDS = 45;
  readonly offerNotificationSettings: NotificationToggleSetting[] = [
    { key: 'email', preferenceKey: 'email' },
    { key: 'sms', preferenceKey: 'sms' },
    { key: 'whatsapp', preferenceKey: 'whatsapp', proOnly: true },
    { key: 'discord', preferenceKey: 'discord' }
  ];
  readonly digestOptions: NotificationPreferences['digest'][] = ['immediate', 'daily', 'weekly', 'none'];
  readonly smsCountryOptions: SmsCountryOption[] = this.buildSmsCountryOptions();
  readonly preferencesSuccessStatusKeys = new Set([
    'settings.preferencesSaved',
    'settings.unsubscribeSaved',
    'settings.processingObjectionSaved',
    'settings.processingObjectionRevoked',
    'settings.smsVerificationRequested',
    'settings.smsVerificationConfirmed'
  ]);

  preferences: NotificationPreferences = this.defaultPreferences();
  isLoadingPreferences = true;
  isSavingPreferences = false;
  preferencesStatusKey = '';
  smsValidationErrorKey = '';
  whatsappValidationErrorKey = '';
  discordValidationErrorKey = '';
  isDiscordWebhookUrlVisible = false;
  isTestingDiscordWebhook = false;
  smsVerificationCode = '';
  smsVerificationCooldownSeconds = 0;
  smsCountrySearch = '';
  currentEntitlements: PlanEntitlements | null = null;
  isLoadingBilling = true;
  isOpeningBillingPortal = false;
  billingStatusKey = '';

  constructor(public i18n: I18nService, public theme: ThemeService, private api: ApiService, private session: SessionService, private router: Router) {}

  ngOnInit(): void {
    void this.loadNotificationPreferences();
    void this.loadBillingSubscription();
  }

  ngOnDestroy(): void {
    this.clearSmsVerificationCooldownTimer();
  }

  toggleDiscordWebhookVisibility(): void {
    this.isDiscordWebhookUrlVisible = !this.isDiscordWebhookUrlVisible;
  }

  setLanguage(language: SupportedLanguage): void {
    void this.savePreferredLanguage(language);
  }

  setDateFormat(format: SupportedDateFormat): void {
    this.i18n.setDateFormat(format);
  }

  private async savePreferredLanguage(language: SupportedLanguage): Promise<void> {
    await this.i18n.setLanguage(language);
    if (!this.session.currentUser()) return;

    try {
      await this.session.updateProfile({ preferredLanguage: language });
    } catch {
      // Keep the local language change even if the profile save fails; the user can retry from settings later.
    }
  }

  setDarkTheme(enabled: boolean): void {
    this.theme.setTheme(enabled ? 'dark' : 'light');
  }

  isNotificationEnabled(preferenceKey: BooleanPreferenceKey): boolean {
    return this.preferences[preferenceKey] === true;
  }

  get hasPreferenceEvidence(): boolean {
    return Boolean(this.preferences.preferencesUpdatedAt || this.preferences.unsubscribedAt || this.preferences.objectedAt || this.preferences.objectionRevokedAt);
  }

  get isSubscribedToNonSecurityNotifications(): boolean {
    return this.preferences.product === true
      && this.preferences.inApp === true
      && this.preferences.immediateAlerts === true
      && this.nonSecurityNotificationKeys.some(key => this.preferences[key] === true);
  }

  get isInNonSecurityUnsubscribedMode(): boolean {
    return this.preferences.product !== true
      || this.preferences.inApp !== true;
  }

  get areNotificationSettingsDisabled(): boolean {
    return this.isLoadingPreferences
      || this.isSavingPreferences
      || this.preferences.stopProcessing === true;
  }

  isNotificationToggleDisabled(channel: NotificationToggleSetting): boolean {
    return this.areNotificationSettingsDisabled || (channel.proOnly === true && !this.isProPlan);
  }

  get notificationSubscriptionActionKey(): string {
    return this.isSubscribedToNonSecurityNotifications ? 'settings.unsubscribe' : 'settings.subscribe';
  }

  get processingObjectionActionKey(): string {
    return this.preferences.stopProcessing ? 'settings.revokeProcessingObjection' : 'settings.objectToProcessing';
  }

  get currentPlanLabel(): string {
    const entitlementPlan = this.currentEntitlements?.plan;
    const entitlementPlanLabel = this.planLabelFromCode(entitlementPlan);
    if (entitlementPlanLabel) return entitlementPlanLabel;

    const sessionPlanLabel = this.planLabelFromTier(this.session.currentUser()?.planTier);
    if (sessionPlanLabel) return sessionPlanLabel;

    return this.currentEntitlements?.displayName
      || this.i18n.t('common.unknown');
  }

  get canManageBilling(): boolean {
    return this.currentEntitlements?.billingManagementAvailable === true || this.isPaidPlan(this.currentPlanCode());
  }

  get isProPlan(): boolean {
    return this.currentPlanCode() === 'PRO';
  }

  async openBillingPortal(): Promise<void> {
    if (this.isOpeningBillingPortal) return;
    this.isOpeningBillingPortal = true;
    this.billingStatusKey = '';
    try {
      if (!(await this.session.ensureSession())) {
        await this.router.navigate(['/login'], { queryParams: { redirectTo: '/settings' } });
        return;
      }
      const response = await this.api.createCustomerPortalSession();
      if (!response.portalUrl) throw new Error('Portal URL was not returned by the API.');
      if (!isTrustedHttpsHostUrl(response.portalUrl, 'billing.stripe.com')) throw new Error('Portal URL was not issued by Stripe Billing.');
      this.navigateToUrl(response.portalUrl);
    } catch (error) {
      console.warn('Unable to open billing portal.', error);
      this.billingStatusKey = 'settings.billing.portalFailed';
    } finally {
      this.isOpeningBillingPortal = false;
    }
  }

  private navigateToUrl(url: string): void {
    window.location.assign(url);
  }

  async setNotificationSetting(preferenceKey: BooleanPreferenceKey, enabled: boolean): Promise<void> {
    await this.setNotificationPreference(preferenceKey, enabled);
  }

  async loadNotificationPreferences(): Promise<void> {
    this.isLoadingPreferences = true;
    this.preferencesStatusKey = '';
    try {
      this.preferences = await this.api.getNotificationPreferences();
      this.preferences = this.withDefaultSmsCountry(this.preferences);
      this.syncSmsVerificationCooldownFromPreferences();
    } catch {
      this.preferences = this.defaultPreferences();
      this.preferencesStatusKey = 'settings.preferencesLoadFailed';
    } finally {
      this.isLoadingPreferences = false;
    }
  }

  async loadBillingSubscription(): Promise<void> {
    this.isLoadingBilling = true;
    this.billingStatusKey = '';
    try {
      if (!(await this.session.ensureSession())) {
        this.currentEntitlements = null;
        return;
      }
      this.currentEntitlements = await this.api.getSubscriptionEntitlements();
    } catch (error) {
      console.warn('Unable to load subscription entitlements.', error);
      this.currentEntitlements = null;
      this.billingStatusKey = 'settings.billing.loadFailed';
    } finally {
      this.isLoadingBilling = false;
    }
  }

  async setNotificationPreference(key: BooleanPreferenceKey, enabled: boolean): Promise<void> {
    if (key === 'whatsapp' && enabled && !this.isProPlan) {
      this.preferences = { ...this.preferences, whatsapp: false };
      this.whatsappValidationErrorKey = 'settings.destinations.whatsappProOnly';
      this.preferencesStatusKey = '';
      return;
    }
    this.preferences = { ...this.preferences, [key]: enabled };
    this.preferencesStatusKey = '';
    if (key === 'sms') this.smsValidationErrorKey = '';
    if (key === 'whatsapp') this.whatsappValidationErrorKey = '';
    if (key === 'discord') this.discordValidationErrorKey = '';
  }

  setNotificationTextPreference(key: TextPreferenceKey, value: string): void {
    this.preferences = { ...this.preferences, [key]: value.trim() };
    this.preferencesStatusKey = '';
    if (key === 'smsPhoneNumber') {
      this.smsValidationErrorKey = '';
      this.preferences = { ...this.preferences, smsPhoneNumberVerified: false, smsVerificationRequired: false };
      this.smsVerificationCode = '';
      this.clearSmsVerificationCooldown();
    }
    if (key === 'whatsappPhoneNumber') this.whatsappValidationErrorKey = '';
    if (key === 'discordUsername') this.discordValidationErrorKey = '';
  }

  setSmsVerificationCode(code: string): void {
    this.smsVerificationCode = code.trim();
    this.preferencesStatusKey = '';
  }

  setSmsCountry(countryCode: string): void {
    const supportedCountry = this.smsCountryOptions.find(country => country.code === countryCode)?.code ?? this.smsCountryOptions[0].code;
    this.preferences = { ...this.preferences, smsCountryCode: supportedCountry };
    this.smsValidationErrorKey = '';
    this.preferencesStatusKey = '';
  }

  setSmsCountrySearch(searchTerm: string): void {
    this.smsCountrySearch = searchTerm;
    const normalizedSearch = searchTerm.trim().toUpperCase();
    const exactCodeMatch = this.smsCountryOptions.find(country => country.code === normalizedSearch);
    const firstMatchingCountry = exactCodeMatch ?? this.filteredSmsCountryOptions()[0];
    if (firstMatchingCountry) this.setSmsCountry(firstMatchingCountry.code);
  }

  filteredSmsCountryOptions(): SmsCountryOption[] {
    const normalizedSearch = this.smsCountrySearch.trim().toLowerCase();
    if (!normalizedSearch) return this.smsCountryOptions;

    const exactCodeMatch = this.smsCountryOptions.find(country => country.code.toLowerCase() === normalizedSearch);
    if (exactCodeMatch) return [exactCodeMatch];

    return this.smsCountryOptions.filter(country => [country.code, country.name, country.dialCode]
      .some(value => value.toLowerCase().includes(normalizedSearch)));
  }

  openDiscordServerSetup(): void {
    window.location.assign('https://support.discord.com/hc/en-us/articles/228383668-Intro-to-Webhooks');
  }

  async setDigest(digest: NotificationPreferences['digest']): Promise<void> {
    this.preferences = { ...this.preferences, digest };
    this.preferencesStatusKey = '';
  }

  async saveNotificationPanel(): Promise<void> {
    if (this.areNotificationSettingsDisabled) return;
    const fullPreferences = this.buildFullNotificationPreferencesPayload();
    if (!this.validateSmsDestination(fullPreferences)) return;
    if (!this.validateWhatsappDestination(fullPreferences)) return;
    if (!this.validateDiscordDestination(fullPreferences)) return;
    await this.saveNotificationPreferences(fullPreferences);
  }

  async toggleNotificationSubscription(): Promise<void> {
    if (this.isSubscribedToNonSecurityNotifications) {
      await this.unsubscribe();
      return;
    }

    await this.subscribeToNonSecurityNotifications();
  }

  async unsubscribe(): Promise<void> {
    if (this.isSavingPreferences) return;
    this.isSavingPreferences = true;
    this.preferencesStatusKey = '';
    try {
      this.preferences = await this.api.unsubscribeNotifications('settings_unsubscribe');
      this.preferencesStatusKey = 'settings.unsubscribeSaved';
    } catch {
      this.preferencesStatusKey = 'settings.preferencesSaveFailed';
    } finally {
      this.isSavingPreferences = false;
    }
  }

  async subscribeToNonSecurityNotifications(): Promise<void> {
    if (this.isSavingPreferences) return;
    const hasExistingChannel = this.nonSecurityNotificationKeys.some(key => this.preferences[key] === true);
    const updates = {
      email: hasExistingChannel ? this.preferences.email : true,
      sms: this.preferences.sms,
      whatsapp: this.isProPlan ? this.preferences.whatsapp : false,
      discord: this.preferences.discord,
      inApp: true,
      product: true,
      immediateAlerts: true,
      security: true,
      stopProcessing: false
    } satisfies Partial<NotificationPreferences>;

    await this.saveNotificationPreferences(updates, 'settings.preferencesSaved');
  }

  async toggleProcessingObjection(): Promise<void> {
    if (this.preferences.stopProcessing) {
      await this.revokeProcessingObjection();
      return;
    }

    await this.objectToProcessing();
  }

  async objectToProcessing(): Promise<void> {
    if (this.isSavingPreferences) return;
    this.isSavingPreferences = true;
    this.preferencesStatusKey = '';
    try {
      this.preferences = await this.api.objectToProcessing('settings_privacy_objection');
      this.preferencesStatusKey = 'settings.processingObjectionSaved';
    } catch {
      this.preferencesStatusKey = 'settings.preferencesSaveFailed';
    } finally {
      this.isSavingPreferences = false;
    }
  }

  async revokeProcessingObjection(): Promise<void> {
    if (this.isSavingPreferences) return;
    this.isSavingPreferences = true;
    this.preferencesStatusKey = '';
    try {
      this.preferences = await this.api.revokeProcessingObjection('settings_revoke_privacy_objection');
      this.preferencesStatusKey = 'settings.processingObjectionRevoked';
    } catch {
      this.preferencesStatusKey = 'settings.preferencesSaveFailed';
    } finally {
      this.isSavingPreferences = false;
    }
  }

  async confirmSmsVerification(): Promise<void> {
    if (this.areNotificationSettingsDisabled || !this.smsVerificationCode.trim()) return;
    const previousPreferences = this.preferences;
    this.isSavingPreferences = true;
    this.preferencesStatusKey = '';
    try {
      await this.api.confirmSmsPhoneVerification(this.smsVerificationCode);
      this.preferences = this.reconcileSavedPreferences(await this.api.getNotificationPreferences(), { smsPhoneNumberVerified: true, smsVerificationRequired: false });
      this.smsVerificationCode = '';
      this.clearSmsVerificationCooldown();
      this.preferencesStatusKey = 'settings.smsVerificationConfirmed';
    } catch {
      this.preferences = previousPreferences;
      this.preferencesStatusKey = 'settings.smsVerificationFailed';
    } finally {
      this.isSavingPreferences = false;
    }
  }

  async resendSmsVerificationCode(): Promise<void> {
    if (this.areNotificationSettingsDisabled || !this.preferences.smsPhoneNumber || this.preferences.smsPhoneNumberVerified || this.smsVerificationCooldownSeconds > 0) return;
    this.isSavingPreferences = true;
    this.preferencesStatusKey = '';
    try {
      const response = await this.api.requestSmsPhoneVerification(this.preferences.smsPhoneNumber);
      this.preferences = this.reconcileSavedPreferences(await this.api.getNotificationPreferences(), { smsVerificationRequired: true, smsPhoneNumberVerified: false });
      this.startSmsVerificationCooldown(response.retryAfterSeconds ?? Settings.SMS_VERIFICATION_RESEND_COOLDOWN_SECONDS);
      this.preferencesStatusKey = 'settings.smsVerificationRequested';
    } catch {
      this.preferencesStatusKey = 'settings.smsVerificationFailed';
    } finally {
      this.isSavingPreferences = false;
    }
  }

  async testDiscordWebhook(): Promise<void> {
    if (this.areNotificationSettingsDisabled || this.isTestingDiscordWebhook) return;
    const payload = this.buildFullNotificationPreferencesPayload();
    if (!this.validateDiscordDestination(payload)) return;

    this.isTestingDiscordWebhook = true;
    this.preferencesStatusKey = '';
    try {
      await this.api.testDiscordWebhook(payload.discordUsername ?? '', payload.discordMessagePrefix ?? '');
      this.preferencesStatusKey = 'settings.destinations.discordTestSent';
    } catch {
      this.preferencesStatusKey = 'settings.destinations.discordTestFailed';
    } finally {
      this.isTestingDiscordWebhook = false;
    }
  }

  private async saveNotificationPreferences(updates: Partial<NotificationPreferences>, successStatusKey = 'settings.preferencesSaved'): Promise<void> {
    if (this.isSavingPreferences) return;
    const previousPreferences = this.preferences;
    this.preferences = { ...this.preferences, ...updates };
    this.isSavingPreferences = true;
    this.preferencesStatusKey = '';
    try {
      this.preferences = this.reconcileSavedPreferences(await this.api.updateNotificationPreferences(updates), updates);
      if (this.preferences.smsVerificationRequired && this.preferences.smsPhoneNumber && !this.preferences.smsPhoneNumberVerified) {
        try {
          const response = await this.api.requestSmsPhoneVerification(this.preferences.smsPhoneNumber);
          this.startSmsVerificationCooldown(response.retryAfterSeconds ?? Settings.SMS_VERIFICATION_RESEND_COOLDOWN_SECONDS);
          this.preferencesStatusKey = 'settings.smsVerificationRequested';
        } catch {
          this.preferencesStatusKey = 'settings.smsVerificationFailed';
        }
      } else {
        this.preferencesStatusKey = successStatusKey;
      }
    } catch {
      this.preferences = previousPreferences;
      this.preferencesStatusKey = 'settings.preferencesSaveFailed';
    } finally {
      this.isSavingPreferences = false;
    }
  }

  private defaultPreferences(): NotificationPreferences {
    return { email: true, sms: false, whatsapp: false, discord: false, inApp: true, digest: 'daily', immediateAlerts: true, marketing: false, product: true, security: true, stopProcessing: false, smsCountryCode: 'FR', smsPhoneNumber: '', smsPhoneNumberVerified: false, smsVerificationRequired: false, whatsappPhoneNumber: '', discordMessagePrefix: '' };
  }

  private readonly nonSecurityNotificationKeys: BooleanPreferenceKey[] = ['email', 'sms', 'whatsapp', 'discord'];

  private reconcileSavedPreferences(response: NotificationPreferences, updates: Partial<NotificationPreferences>): NotificationPreferences {
    return this.withDefaultSmsCountry({ ...response, ...updates });
  }

  private buildFullNotificationPreferencesPayload(): NotificationPreferences {
    const country = this.selectedSmsCountry();
    const smsPhoneNumber = this.preferences.sms ? this.normalizedSmsPhoneNumber() : (this.preferences.smsPhoneNumber?.trim() ?? '');
    const whatsappEnabled = this.isProPlan && this.preferences.whatsapp === true;
    const whatsappPhoneNumber = whatsappEnabled ? this.normalizedWhatsappPhoneNumber() : '';
    return {
      email: this.preferences.email === true,
      sms: this.preferences.sms === true,
      whatsapp: whatsappEnabled,
      discord: this.preferences.discord === true,
      inApp: true,
      digest: this.preferences.digest || 'daily',
      immediateAlerts: true,
      marketing: false,
      product: true,
      security: true,
      stopProcessing: this.preferences.stopProcessing === true,
      smsCountryCode: country.code,
      smsPhoneNumber,
      smsPhoneNumberVerified: this.preferences.smsPhoneNumberVerified === true,
      whatsappPhoneNumber,
      discordUsername: this.preferences.discord ? this.normalizedDiscordWebhookUrl() : '',
      discordMessagePrefix: this.preferences.discord ? this.normalizedDiscordMessagePrefix() : ''
    };
  }

  private normalizedDiscordWebhookUrl(): string {
    return (this.preferences.discordUsername ?? '').trim();
  }

  private normalizedDiscordMessagePrefix(): string {
    return (this.preferences.discordMessagePrefix ?? '').trim().slice(0, 200);
  }

  selectedSmsCountry(): SmsCountryOption {
    return this.smsCountryOptions.find(country => country.code === this.preferences.smsCountryCode) ?? this.smsCountryOptions[0];
  }

  countryFlag(countryCode: string): string {
    return countryFlag(countryCode);
  }

  smsPlaceholder(): string {
    return 'phone number';
  }

  smsDialCode(): string {
    return this.selectedSmsCountry().dialCode;
  }

  private validateSmsDestination(preferences: NotificationPreferences): boolean {
    this.smsValidationErrorKey = '';
    if (!preferences.sms) return true;
    const phoneNumber = this.parsedSmsPhoneNumber();
    if (!this.preferences.smsPhoneNumber?.trim()) {
      this.smsValidationErrorKey = 'settings.destinations.smsRequired';
      return false;
    }
    if (!phoneNumber?.isValid()) {
      this.smsValidationErrorKey = 'settings.destinations.smsInvalid';
      return false;
    }
    return true;
  }

  private validateWhatsappDestination(preferences: NotificationPreferences): boolean {
    this.whatsappValidationErrorKey = '';
    if (!preferences.whatsapp) return true;
    const phoneNumber = this.parsedWhatsappPhoneNumber();
    if (!this.preferences.whatsappPhoneNumber?.trim()) {
      this.whatsappValidationErrorKey = 'settings.destinations.whatsappRequired';
      return false;
    }
    if (!phoneNumber?.isValid()) {
      this.whatsappValidationErrorKey = 'settings.destinations.whatsappInvalid';
      return false;
    }
    return true;
  }

  private validateDiscordDestination(preferences: NotificationPreferences): boolean {
    this.discordValidationErrorKey = '';
    if (!preferences.discord) return true;

    const webhookUrl = preferences.discordUsername?.trim() ?? '';
    if (!webhookUrl) {
      this.discordValidationErrorKey = 'settings.destinations.discordRequired';
      return false;
    }
    if (!this.isValidDiscordWebhookUrl(webhookUrl)) {
      this.discordValidationErrorKey = 'settings.destinations.discordInvalid';
      return false;
    }
    return true;
  }

  private isValidDiscordWebhookUrl(webhookUrl: string): boolean {
    try {
      const url = new URL(webhookUrl);
      if (url.protocol !== 'https:') return false;
      const allowedHosts = ['discord.com', 'discordapp.com', 'canary.discord.com', 'ptb.discord.com'];
      if (!allowedHosts.includes(url.hostname.toLowerCase())) return false;

      const pathSegments = url.pathname.split('/').filter(Boolean);
      if (pathSegments.length < 4) return false;
      if (pathSegments[0] !== 'api' || pathSegments[1] !== 'webhooks') return false;
      if (!/^\d{17,25}$/.test(pathSegments[2])) return false;
      return /^[A-Za-z0-9._-]{20,}$/.test(pathSegments[3]);
    } catch {
      return false;
    }
  }

  private normalizedSmsPhoneNumber(): string {
    return this.parsedSmsPhoneNumber()?.number ?? '';
  }

  private normalizedWhatsappPhoneNumber(): string {
    return this.parsedWhatsappPhoneNumber()?.number ?? '';
  }

  private parsedSmsPhoneNumber(): ReturnType<typeof parsePhoneNumberFromString> {
    const country = this.selectedSmsCountry();
    return parsePhoneNumberFromString(this.normalizedSmsInput(), country.code);
  }

  private parsedWhatsappPhoneNumber(): ReturnType<typeof parsePhoneNumberFromString> {
    const country = this.selectedSmsCountry();
    return parsePhoneNumberFromString(this.normalizedWhatsappInput(), country.code);
  }

  private normalizedSmsInput(): string {
    return this.normalizedPhoneInput(this.preferences.smsPhoneNumber ?? '');
  }

  private normalizedWhatsappInput(): string {
    return this.normalizedPhoneInput(this.preferences.whatsappPhoneNumber ?? '');
  }

  private normalizedPhoneInput(value: string): string {
    const country = this.selectedSmsCountry();
    const rawPhoneNumber = value.trim();
    if (!rawPhoneNumber) return '';
    if (rawPhoneNumber.startsWith('+')) return rawPhoneNumber;

    const digitsOnly = rawPhoneNumber.replace(/[^\d]/g, '');
    if (digitsOnly.startsWith('00')) return `+${digitsOnly.slice(2)}`;

    const dialCodeDigits = country.dialCode.slice(1);
    if (digitsOnly.startsWith(dialCodeDigits)) return digitsOnly.slice(dialCodeDigits.length);

    return rawPhoneNumber;
  }

  private buildSmsCountryOptions(): SmsCountryOption[] {
    const locale = typeof this.i18n.dateLocale === 'function' ? this.i18n.dateLocale() : 'en';

    return getCountries()
      .map(code => ({
        code,
        name: countryDisplayName(code, locale),
        dialCode: `+${getCountryCallingCode(code)}`
      }))
      .sort((left, right) => left.name.localeCompare(right.name));
  }

  private withDefaultSmsCountry(preferences: NotificationPreferences): NotificationPreferences {
    return { ...preferences, smsCountryCode: preferences.smsCountryCode ?? 'FR' };
  }

  private smsVerificationCooldownTimer: ReturnType<typeof setInterval> | null = null;

  private syncSmsVerificationCooldownFromPreferences(): void {
    const requestedAt = Date.parse(this.preferences.smsVerificationRequestedAt ?? '');
    if (!Number.isFinite(requestedAt) || this.preferences.smsPhoneNumberVerified || !this.preferences.smsVerificationRequired) {
      this.clearSmsVerificationCooldown();
      return;
    }

    const elapsedSeconds = Math.floor((Date.now() - requestedAt) / 1000);
    this.startSmsVerificationCooldown(Math.max(0, Settings.SMS_VERIFICATION_RESEND_COOLDOWN_SECONDS - elapsedSeconds));
  }

  private startSmsVerificationCooldown(seconds: number): void {
    const normalizedSeconds = Math.max(0, Math.ceil(seconds));
    this.clearSmsVerificationCooldownTimer();
    this.smsVerificationCooldownSeconds = normalizedSeconds;
    if (normalizedSeconds <= 0) return;

    this.smsVerificationCooldownTimer = setInterval(() => {
      this.smsVerificationCooldownSeconds = Math.max(0, this.smsVerificationCooldownSeconds - 1);
      if (this.smsVerificationCooldownSeconds <= 0) this.clearSmsVerificationCooldownTimer();
    }, 1000);
  }

  private clearSmsVerificationCooldown(): void {
    this.clearSmsVerificationCooldownTimer();
    this.smsVerificationCooldownSeconds = 0;
  }

  private clearSmsVerificationCooldownTimer(): void {
    if (this.smsVerificationCooldownTimer !== null) {
      clearInterval(this.smsVerificationCooldownTimer);
      this.smsVerificationCooldownTimer = null;
    }
  }

  private currentPlanCode(): string {
    const entitlementPlan = String(this.currentEntitlements?.plan ?? '').trim().toUpperCase();
    if (entitlementPlan) return entitlementPlan;
    return this.planCodeFromTier(this.session.currentUser()?.planTier) ?? '';
  }

  private isPaidPlan(plan: string): boolean {
    return plan === 'BASE' || plan === 'PRO';
  }

  private planLabelFromCode(plan: unknown): string | null {
    const code = String(plan ?? '').trim().toUpperCase();
    if (code === 'FREE') return 'Free';
    if (code === 'BASE') return 'Candidate';
    if (code === 'PRO') return 'Research Pro';
    return null;
  }

  private planLabelFromTier(planTier: unknown): string | null {
    const code = this.planCodeFromTier(planTier);
    return code ? this.planLabelFromCode(code) : null;
  }

  private planCodeFromTier(planTier: unknown): string | null {
    const normalized = String(planTier ?? '').trim().toLowerCase();
    if (!normalized) return null;
    if (['free', 'starter'].includes(normalized)) return 'FREE';
    if (['base', 'candidate'].includes(normalized)) return 'BASE';
    if (['pro', 'researchpro', 'research_pro', 'research pro'].includes(normalized)) return 'PRO';
    return null;
  }
}
